import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useParams, useLocation } from 'react-router-dom';
import Seo, { DEFAULT_LOGO } from './Seo';

const BlogPost = () => {
  const { slug } = useParams();
  const location = useLocation();

  const [content, setContent] = useState('Loading...');
  const [metadata, setMetadata] = useState({ title: '', description: '', date: '', author: '' });
  const [error, setError] = useState(false);

  useEffect(() => {
    setError(false);
    fetch(`/posts/${slug}.md`)
      .then((res) => {
        if (!res.ok) throw new Error('Post not found');
        return res.text();
      })
      .then((text) => {
        // Parse frontmatter manually (assuming frontmatter is at the top of the markdown)
        const frontmatterMatch = text.match(/---\n([\s\S]+?)\n---/);
        let frontmatter = {};
        let markdownContent = text;

        if (frontmatterMatch) {
          const yaml = frontmatterMatch[1];
          markdownContent = text.slice(frontmatterMatch[0].length).trim();

          // Simple YAML parsing for basic fields (title, description, date, author)
          yaml.split('\n').forEach((line) => {
            const [key, ...rest] = line.split(':');
            if (key && rest) {
              frontmatter[key.trim()] = rest.join(':').trim().replace(/^"(.*)"$/, '$1'); // remove quotes
            }
          });
        }

        setMetadata({
          title: frontmatter.title || '',
          description: frontmatter.description || '',
          date: frontmatter.date || '',
          author: frontmatter.author || '',
        });
        setContent(markdownContent);
      })
      .catch(() => {
        setError(true);
        setContent('Sorry, this post does not exist.');
      });
  }, [slug]);

  // Format date nicely
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateStr).toLocaleDateString(undefined, options);
  };

  // Build canonical URL dynamically
  const canonicalUrl = `https://www.compressimages.org${location.pathname}`;

  return (
    <div className="page pt-32">
      <Seo
        title={metadata.title || 'Blog Post | CompressImages.org'}
        description={metadata.description || 'Image compression guide from CompressImages.org.'}
        path={location.pathname}
        type="article"
        jsonLd={metadata.title ? {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: metadata.title,
          description: metadata.description,
          datePublished: metadata.date,
          author: {
            '@type': 'Organization',
            name: metadata.author || 'CompressImages.org',
          },
          publisher: {
            '@type': 'Organization',
            name: 'CompressImages.org',
            logo: {
              '@type': 'ImageObject',
              url: DEFAULT_LOGO,
            },
          },
          mainEntityOfPage: canonicalUrl,
        } : undefined}
      />

      <article className="article-wrap">
      <div className="article-tag">Guide</div>
      {metadata.title && <h1 className="article-title">{metadata.title}</h1>}

      {metadata.author && metadata.date && (
        <p className="article-byline">
          By <span className="font-medium">{metadata.author}</span> • <time dateTime={metadata.date}>{formatDate(metadata.date)}</time>
        </p>
      )}

      <div className="article-body">
        {error ? <p>{content}</p> : <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>}
      </div>
      </article>
    </div>
  );
};

export default BlogPost;
